import React from 'react';
import classNames from 'classnames';
import { SectionTilesProps } from '../../utils/SectionProps';
import { useLanguage } from '../../contexts/LanguageContext';

const propTypes = {
  ...SectionTilesProps.types,
};

const defaultProps = {
  ...SectionTilesProps.defaults,
};

const CapabilityIcon = ({ type }) => {
  const icons = {
    moodle: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"
          fill="#24E5AF"
        />
      </svg>
    ),
    agent: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zM9 14a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"
          fill="#e2e625"
        />
      </svg>
    ),
    steps: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        {/* Connector running through the three step markers. */}
        <path d="M6 6v12" stroke="#9CA9B3" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
        {/* Steps one and two: pending, in muted grey. */}
        <circle cx="6" cy="6" r="2.25" stroke="#9CA9B3" strokeWidth="1.5" />
        <rect x="10.5" y="5" width="10" height="2" rx="1" fill="#9CA9B3" opacity="0.6" />
        <circle cx="6" cy="12" r="2.25" stroke="#9CA9B3" strokeWidth="1.5" />
        <rect x="10.5" y="11" width="10" height="2" rx="1" fill="#9CA9B3" opacity="0.6" />
        {/* Step three is done: filled teal marker with a check. */}
        <circle cx="6" cy="18" r="2.75" fill="#24E5AF" />
        <path
          d="M4.9 18l.8.85L7.15 17.3"
          stroke="#0D0F11"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="10.5" y="17" width="10" height="2" rx="1" fill="#24E5AF" />
      </svg>
    ),
  };
  return icons[type] || null;
};

const FeaturesTiles = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  pushLeft,
  ...props
}) => {
  const outerClasses = classNames(
    'features-tiles section capabilities-section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className,
  );

  const innerClasses = classNames(
    'features-tiles-inner section-inner pt-0',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider',
  );

  const tilesClasses = classNames('tiles-wrap center-content', pushLeft && 'push-left');

  const { lang } = useLanguage();

  const content = {
    en: {
      label: 'What We Build',
      title: 'A tutor inside your course.',
      subtitle:
        'Your students ask on the same page where they study, and the agent answers from your course material. No new platform, no new login.',
      tiles: [
        {
          icon: 'moodle',
          title: 'Answers Only From Your Course',
          desc: 'The agent answers from your course material and nothing else. Every answer cites the unit and the page, and it says so when a question is not covered in the course.',
        },
        {
          icon: 'agent',
          title: 'Native Inside Moodle',
          desc: 'The agent lives in the Moodle course your students already open. Nothing to install, no app to switch to, no extra platform to administer.',
        },
        {
          icon: 'steps',
          title: 'Live in Three Steps',
          desc: 'We connect the course, index the material, and the agent goes live inside Moodle, answering your students 24 hours a day.',
        },
      ],
    },
    es: {
      label: 'Qué Construimos',
      title: 'Un tutor dentro de tu curso.',
      subtitle:
        'Tus alumnos preguntan en la misma página donde estudian y el agente responde con el material de tu curso. Sin plataforma nueva, sin login nuevo.',
      tiles: [
        {
          icon: 'moodle',
          title: 'Solo con el material del curso',
          desc: 'El agente responde con el material de tu curso y nada más. Cada respuesta cita la unidad y la página, y si la pregunta no está en el curso, lo dice.',
        },
        {
          icon: 'agent',
          title: 'Nativo dentro de Moodle',
          desc: 'El agente vive en el curso de Moodle que tus alumnos ya abren. Nada que instalar, ninguna app a la que cambiar, ninguna plataforma extra que administrar.',
        },
        {
          icon: 'steps',
          title: 'Activo en tres pasos',
          desc: 'Conectamos el curso, indexamos el material y el agente queda activo dentro de Moodle, respondiendo a tus alumnos las 24 horas.',
        },
      ],
    },
  };

  const c = content[lang];

  return (
    <section {...props} className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          <div className="center-content reveal-from-bottom" style={{ marginBottom: '64px' }}>
            <span className="section-label">{c.label}</span>
            <h2
              className="mt-0 mb-16"
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                fontWeight: 800,
                letterSpacing: '-0.02em',
              }}
            >
              {c.title}
            </h2>
            <p
              style={{
                color: 'var(--ec-muted)',
                maxWidth: '520px',
                margin: '0 auto',
                lineHeight: 1.7,
                fontSize: '0.95rem',
              }}
            >
              {c.subtitle}
            </p>
          </div>
          <div className={tilesClasses}>
            {c.tiles.map((tile, i) => (
              <div className="tiles-item reveal-from-bottom" key={i} data-reveal-delay={i * 150}>
                <div className="tiles-item-inner">
                  <div className="features-tiles-item-header">
                    <div className="features-tiles-item-image mb-16">
                      <CapabilityIcon type={tile.icon} />
                    </div>
                  </div>
                  <div className="features-tiles-item-content">
                    <h4 className="mt-0 mb-8">{tile.title}</h4>
                    <p className="m-0 text-sm">{tile.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

FeaturesTiles.propTypes = propTypes;
FeaturesTiles.defaultProps = defaultProps;

export default FeaturesTiles;
